import React from 'react';
import { Alert } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { Block } from '../../../Courses/editors/BlockEditor/types';
import { normalizeBlocks } from '../../../Courses/editors/BlockEditor/normalizeBlocks';
import {
    AudioPlayer, CalloutPlayer, CodePlayer, DividerPlayer, EmbedPlayer, FilePlayer, HeadingPlayer, ImagePlayer, PdfViewerPlayer, QuotePlayer, RichtextPlayer, TablePlayer, TimelinePlayer, VideoPlayer,
} from './ContentBlockPlayers';
import { AccordionPlayer, ColumnsPlayer, TabsPlayer } from './LayoutBlockPlayers';
import { FillBlankPlayer, FlashcardPlayer, MatchingPlayer, OrderingPlayer, PollPlayer, QuizPlayer } from './InteractiveBlockPlayers';
import { BranchingPlayer, DiscussionPlayer, H5pPlayer, HotspotPlayer, LabelingPlayer, ScormEmbedPlayer, SimulationPlayer, SliderScalePlayer, SortingPlayer } from './ExtendedBlockPlayers';
import './block-player.css';

/**
 * Renders one block for learners (ported from Scilora `block-player.tsx`): plain
 * React from the Block JSON, no TipTap. Interactive blocks talk to the server
 * through BlockInteractionContext; unknown types show a neutral placeholder.
 */
export const BlockPlayer: React.FC<{ block: Block }> = ({ block }) => {
    const translate = useTranslate();
    const [normalized] = normalizeBlocks([block]);
    const d = normalized.data;

    switch (block.type) {
        case 'richtext':
            return <RichtextPlayer html={d.html as string} />;
        case 'heading':
            return <HeadingPlayer text={d.text as string} level={d.level as number} />;
        case 'image':
            return <ImagePlayer src={d.src as string} alt={d.alt as string} caption={d.caption as string} alignment={d.alignment as string} />;
        case 'video':
            return <VideoPlayer src={d.src as string} poster={d.poster as string} caption={d.caption as string} />;
        case 'audio':
            return <AudioPlayer src={d.src as string} title={d.title as string} />;
        case 'file':
            return <FilePlayer src={d.src as string} filename={d.filename as string} filesize={d.filesize as number} />;
        case 'embed':
            return <EmbedPlayer url={d.url as string} caption={d.caption as string} aspectRatio={(d.aspectRatio as string) ?? '16:9'} />;
        case 'code':
            return <CodePlayer code={d.code as string} language={d.language as string} />;
        case 'divider':
            return <DividerPlayer />;
        case 'callout':
            return <CalloutPlayer html={d.html as string} variant={(d.variant as string) ?? 'info'} />;
        case 'quote':
            return <QuotePlayer text={d.text as string} attribution={d.attribution as string} />;
        case 'table':
            return <TablePlayer rows={d.rows as string[][]} hasHeader={d.hasHeader as boolean} caption={d.caption as string} />;
        case 'timeline':
            return <TimelinePlayer entries={d.entries as { date: string; title: string; description: string }[]} />;
        case 'pdf_viewer':
            return <PdfViewerPlayer src={d.src as string} filename={d.filename as string} />;
        case 'columns':
            return <ColumnsPlayer columns={d.columns as { width?: string; blocks: Block[] }[]} />;
        case 'accordion':
            return <AccordionPlayer items={d.items as { title: string; blocks: Block[]; defaultOpen?: boolean }[]} allowMultiple={d.allowMultiple as boolean} />;
        case 'tabs':
            return <TabsPlayer tabs={d.tabs as { label: string; blocks: Block[] }[]} />;
        case 'inline_quiz':
            return <QuizPlayer blockId={block.id} data={d} />;
        case 'fill_blank':
            return <FillBlankPlayer blockId={block.id} data={d} />;
        case 'matching':
            return <MatchingPlayer blockId={block.id} data={d} />;
        case 'ordering':
            return <OrderingPlayer blockId={block.id} data={d} />;
        case 'flashcard':
            return <FlashcardPlayer data={d} />;
        case 'poll':
            return <PollPlayer blockId={block.id} data={d} />;
        case 'hotspot':
            return <HotspotPlayer blockId={block.id} data={d} />;
        case 'labeling':
            return <LabelingPlayer blockId={block.id} data={d} />;
        case 'sorting':
            return <SortingPlayer blockId={block.id} data={d} />;
        case 'slider_scale':
            return <SliderScalePlayer blockId={block.id} data={d} />;
        case 'discussion':
            return <DiscussionPlayer blockId={block.id} data={d} />;
        case 'branching':
            return <BranchingPlayer blockId={block.id} data={d} />;
        case 'simulation':
            return <SimulationPlayer blockId={block.id} data={d} />;
        case 'scorm_embed':
            return <ScormEmbedPlayer blockId={block.id} data={d} />;
        case 'h5p':
            return <H5pPlayer blockId={block.id} data={d} />;
        default:
            return <Alert severity="info" variant="outlined" sx={{ my: 1 }}>{translate('blockPlayer.unsupported', { type: block.type })}</Alert>;
    }
};

/** Renders a block list in document order (used for lessons and nested layout blocks). */
export const BlockList: React.FC<{ blocks: Block[] }> = ({ blocks }) => (
    <div className="oa-bp-list">
        {(blocks ?? []).map((b, i) => (
            <div key={b.id || i} className="oa-bp-block" data-block-id={b.id} data-block-type={b.type}>
                <BlockPlayer block={b} />
            </div>
        ))}
    </div>
);

export default BlockPlayer;
